import { useEffect } from 'react'

import {
  canBeDefaultLine,
  isImessageProvider,
  useActiveLineStore,
  useDefaultLine,
} from '#/entities/telephony'
import { useSidePanelStore } from '#/shared/lib/side-panel'
import { useTelephonyStore } from '#/shared/lib/telephony'
import type { MyLine } from '#/shared/api/generated/communication/models'
import { formatPhone } from '#/shared/lib/format'

export interface PhoneLineOption {
  /** UserPhone id required by the send-SMS / dial requests. */
  id: string
  /** Raw E.164 number, used as the outbound caller id. */
  e164: string
  /** Formatted number, e.g. "(415) 404-9036". */
  phone: string
  /** Line label, e.g. "Main"; empty when unlabeled. */
  label: string
  /** Switcher text, e.g. "(415) 404-9036 · Main". */
  display: string
  /** May place outbound calls from this line (`outbound_call`). */
  canCall: boolean
  /** May send outbound texts from this line (`outbound_sms`). */
  canText: boolean
  /** May be persisted as the account default. False for mainlines (the backend
   *  rejects it) — pick those as the active line without persisting. */
  canSetDefault: boolean
  /** iMessage (Speko) line — badge it and hide the MMS attach affordance. */
  isImessage: boolean
}

function toOption(line: MyLine): PhoneLineOption {
  const phone = formatPhone(line.phone_number)
  const label = line.label ?? ''
  return {
    id: line.id,
    e164: line.phone_number,
    phone,
    label,
    display: phone && label ? `${phone} · ${label}` : phone,
    canCall: line.outbound_call,
    canText: line.outbound_sms,
    canSetDefault: canBeDefaultLine(line),
    isImessage: isImessageProvider(line.provider),
  }
}

interface ActiveLine {
  lines: PhoneLineOption[]
  active: PhoneLineOption | null
  /** The lines fetch has completed (vs. deferred while the panel is closed). */
  loaded: boolean
  /** Pick the line for the next sends/calls on this tab WITHOUT persisting it as
   *  the account default — the composer "From" picker, a per-message override. */
  select: (id: string) => void
  /** Pick AND persist the account default (`POST .../default`) — the prominent
   *  line control (phone-panel header / keypad "calling from"). */
  setDefault: (id: string) => void
  /** True when there is more than one line — drives the dropdown affordance. */
  hasMultiple: boolean
  isLoading: boolean
}

/**
 * The caller's outbound-capable lines plus the one active selection shared by
 * texting and calling. Two ways to change it: `select` flips the active line
 * locally (per-message override, e.g. the composer "From" picker — never touches
 * the account default); `setDefault` persists the pick as the account default
 * via the shared {@link useDefaultLine} (the header / keypad line control). Read
 * `active.canCall` / `active.canText` to gate the call / SMS actions.
 */
export function useActiveLine(): ActiveLine {
  // The phone panel is always mounted; gate the lines fetch on the panel being
  // open so a closed panel doesn't hit `/telephony/numbers/mine` on every
  // dashboard load. When opened, the day-cached entry (also warmed by the
  // telephony auto-connect) is reused with no extra request.
  const panelOpen = useSidePanelStore((state) => state.isOpen)
  const {
    lines: myLines,
    line,
    setDefault,
    hasMultiple,
    isLoading,
    loaded,
  } = useDefaultLine({ enabled: panelOpen })
  const select = useActiveLineStore((state) => state.select)

  return {
    lines: myLines.map(toOption),
    active: line ? toOption(line) : null,
    loaded,
    select,
    setDefault,
    hasMultiple,
    isLoading,
  }
}

/**
 * The line calls dial out from. An explicit header pick is respected even when
 * it can't place calls (the tooltip says why instead of silently swapping the
 * caller id); but when the line was only auto-resolved (no pick, or a stale
 * pick that fell back), an SMS-only default must not brick calling — use the
 * first call-capable line instead.
 */
function resolveCallLine(
  active: PhoneLineOption | null,
  lines: PhoneLineOption[],
  selectedId: string | null,
): PhoneLineOption | null {
  if (active?.canCall) return active
  const explicitPick = active !== null && active.id === selectedId
  if (explicitPick) return null
  return lines.find((line) => line.canCall) ?? null
}

/**
 * Resolve the call line once and mirror it into the telephony store, where the
 * shared `useStartCall` — behind every dial surface, not just the panel —
 * reads it. Mounted once at the panel root (always mounted in the dashboard).
 * Until the lines fetch has run, `canCall` stays permissively true with no
 * caller id, so surfaces keep dialing from the primary number instead of
 * bricking on a deferred query; keeps the last pick while the panel is closed.
 */
export function useSyncOutboundLine(): void {
  const { active, lines, loaded, isLoading } = useActiveLine()
  const selectedId = useActiveLineStore((state) => state.selectedId)
  const setOutboundLine = useTelephonyStore((s) => s.setOutboundLine)
  const callLine = resolveCallLine(active, lines, selectedId)
  const callerId = callLine?.e164 ?? ''
  // Permissive only while the query is *deferred* — an in-flight load keeps
  // the gate closed with the 'loadingLines' reason instead.
  const canCall = callLine !== null || (!loaded && !isLoading)
  const loading = isLoading && lines.length === 0
  useEffect(() => {
    setOutboundLine({ callerId, canCall, loading })
  }, [callerId, canCall, loading, setOutboundLine])
}
